import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { staffApi } from "../Apis/Api";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import { TextWrap } from "../utils/textWrap";
import PaginationControls from "../components/ui/PaginationControls";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

const StaffBranchesPage = () => {
  const navigate = useNavigate();
  const [staffBranches, setStaffBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    fetchStaffBranches();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchStaffBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        assign_id: "assign_id",
        staff_name: "staff_name",
        staff_email: "staff_email",
        staff_phone: "staff_phone",
        branch_name: "branch_name",
        location_name: "location_name",
        is_active: "is_active",
        created_by: "created_by",
        created_datetime: "created_datetime",
      };

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(filters.status && {
          is_active: filters.status === "Active" ? true : false,
        }),
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await staffApi.getAssignedStaff(params);

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: data is not an array");
      }
      if (!response.pagination || typeof response.pagination !== "object") {
        throw new Error("Invalid API response: pagination data missing");
      }

      handleApiResponse(response, "success", "GET");

      let data = response.data.map((staff, index) => ({
        id: staff.staff_id || index + 1,
        assign_id: staff.assign_id || "-",
        staff_name: staff.staff_name || "-",
        staff_email: staff.staff_email || "-",
        staff_phone: staff.staff_phone || "-",
        branch_name: staff.branch_name || "-",
        location_name: staff.location_name || "-",
        is_active:
          staff.is_active !== undefined && staff.is_active !== null
            ? staff.is_active
              ? "Active"
              : "Inactive"
            : "-",
        created_by: staff.created_by || "-",
        created_datetime: staff.created_datetime || "-",
        display_created_datetime: staff.created_datetime
          ? formatDateTime(staff.created_datetime)
          : "-",
      }));

      if (sortConfig.field) {
        data = [...data].sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setStaffBranches(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
      setError(null);
    } catch (err) {
      setStaffBranches([]);
      handleApiResponse(err, "Failed to fetch Data", "GET");
      setError(err.message || "Failed to fetch Data");
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (staff) => {
    navigate("/staff-edit", { state: { staffData: staff } });
  };

  const handleDeleteClick = (staffId, staffName) => {
    setStaffToDelete({ id: staffId, name: staffName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (staffToDelete) {
      setLoading(true);
      try {
        const res = await staffApi.deleteAssignedStaff(staffToDelete.id);
        handleApiResponse(res, "success", "DELETE");

        setStaffBranches((prev) =>
          prev.filter((staff) => staff.assign_id !== staffToDelete.id)
        );
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          currentPage: Math.min(
            prev.currentPage,
            Math.ceil((prev.total - 1) / prev.perPage) || 1
          ),
        }));
        setShowDeleteModal(false);
        setStaffToDelete(null);
      } catch (err) {
        handleApiResponse(err, "ERR", "DELETE");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setStaffToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setStaffToDelete(null);
  };

  const handleLocationClick = (staff) => {
    navigate("/add-staff-location", {
      state: {
        staffData: {
          id: staff.id,
          name: staff.staff_name,
          branch: staff.branch_name,
        },
      },
    });
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchStaffBranches} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by Staff Name, Email"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search staff by name, email, or branch"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2"> {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Staff Branch List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Assign Staff"
              permissionKey="assign_staff_branch"
              onClick={() => navigate("/staff-branch-assign")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Staff Branch List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {staffBranches.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No staff match your search or filter."
                  : "No assigned staff available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("staff_name")}
                    role="button"
                    aria-label={`Sort by staff name, currently sorted ${
                      sortConfig.field === "staff_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Staff Name
                      <SortIcons field="staff_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("staff_email")}
                    role="button"
                    aria-label={`Sort by staff email, currently sorted ${
                      sortConfig.field === "staff_email"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Staff Email
                      <SortIcons field="staff_email" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("staff_phone")}
                    role="button"
                    aria-label={`Sort by staff phone, currently sorted ${
                      sortConfig.field === "staff_phone"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Staff Phone
                      <SortIcons field="staff_phone" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_name")}
                    role="button"
                    aria-label={`Sort by branch name, currently sorted ${
                      sortConfig.field === "branch_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Branch Name
                      <SortIcons field="branch_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("location_name")}
                    role="button"
                    aria-label={`Sort by location name, currently sorted ${
                      sortConfig.field === "location_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Location Name
                      <SortIcons
                        field="location_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {staffBranches.map((staff, index) => (
                  <tr key={`${staff.id}-${index}`}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {staff.staff_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {staff.staff_email}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {staff.staff_phone}
                      </span>
                    </td>
                    <td>
                      <span
                        className="text-md mb-0 fw-normal text-secondary-light cursor-pointer text-primary-600 hover:text-primary-800"
                        onClick={() => navigate("/branch-list")}
                      >
                        {staff.branch_name}
                      </span>
                    </td>
                    <td>
                      <span
                        className="text-md mb-0 fw-normal text-secondary-light cursor-pointer text-primary-600 hover:text-primary-800"
                        onClick={() => navigate("/branch-locations")}
                      >
                        {staff.location_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {staff.created_by}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={staff.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {staff.display_created_datetime}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("delete_staff_branch_assignment") && (
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteClick(
                                staff.assign_id,
                                staff.staff_name
                              )
                            }
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Delete ${staff.staff_name}`}
                            aria-label={`Delete staff ${staff.staff_name}`}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>{staffToDelete?.name || "this staff"}</strong>?
              </p>
              <p className="text-neutral-900 small">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete staff ${staffToDelete?.name || ""}`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
    <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Staff Assignment Overview"
>
  <p className="text-neutral-900 mb-4">
    This section displays all <strong>staff members</strong> currently working in your organization.
    View employee details including their assigned branch and location.
  </p>
  
  <h6 className="fw-bold text-primary-600 mb-3">What You'll See Here</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Staff Name</strong> – Full name of the employee</li>
    <li className="list-group-item"><strong>Staff Email</strong> – Employee's official email address</li>
    <li className="list-group-item"><strong>Staff Phone</strong> – Contact number for the employee</li>
    <li className="list-group-item"><strong>Branch</strong> – Which branch the staff member is assigned to</li>
    <li className="list-group-item"><strong>Location</strong> – Specific office location where they work</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Available Actions</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Assign Staff (Blue Button)</strong><br/>
      <span className="text-success-600 fw-bold">Main Action</span><br/>
      Click to assign a new staff member to a branch and location.<br/>
      This defines where the employee will be working.<br/>
      <small className="text-danger-600">Requires appropriate permission</small>
    </li>
    <li className="list-group-item">
      <strong>Delete (Red Trash Icon)</strong><br/>
      Remove a staff member's branch/location assignment.<br/>
      Use this when an employee transfers or leaves.
    </li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">How Staff Assignment Works</h6>
  <div className="alert alert-light border mb-4">
    <ol className="mb-0 ps-3">
      <li className="mb-2">Click <strong>"Assign Staff"</strong> button</li>
      <li className="mb-2">Select the staff member from your employee list</li>
      <li className="mb-2">Choose their branch</li>
      <li className="mb-2">Choose their specific location within that branch</li>
      <li>Save to complete the assignment</li>
    </ol>
  </div>

  <div className="alert alert-info small mt-4">
    <strong>Important Notes:</strong><br/>
    • This manages <strong>staff-to-branch/location</strong> relationships only<br/>
    • It is separate from incentive plan assignments<br/>
    • Every staff member should have a branch and location assigned<br/>
    • If no staff is shown, the list will display "No staff assigned yet"
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Keep staff assignments up-to-date for accurate reporting and management!
  </p>
  
  <p className="text-neutral-900 small">
    Business Impact: Proper staff-branch assignments help track performance, attendance, and organizational structure.
  </p>
</InfoModal>
    </>
  );
};

export default StaffBranchesPage;